import { useEffect, useRef, useState } from 'react';
import type { RichAircraft } from '../data/enrich';
import { useStore } from '../store';
import { fmtAlt, fmtDist } from '../lib/format';
import { projectAircraft, cardinals, type DevicePose, type ProjectedAircraft } from './geometry';
import { orientationToPose } from './orientation';
import { ArMinimap } from './ArMinimap';
import { SkyModelRenderer } from './skyModelRenderer';

interface Props {
  aircraft: RichAircraft[];
}

const DEFAULT_FOV_X = 62;
const DEFAULT_FOV_Y = 48;
const DEFAULT_RANGE_NM = 50;

declare global {
  interface Window {
    __fsStartAr?: () => void;
  }
}

export function SkyHud({ aircraft }: Props) {
  const arMode = useStore((s) => s.arMode);
  const setArMode = useStore((s) => s.setArMode);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const skyRendererRef = useRef<SkyModelRenderer | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const poseRef = useRef<DevicePose>({ azimuth: 0, elevation: 0, roll: 0 });
  const userRef = useRef<{ lat: number; lon: number; altM: number } | null>(null);
  const aircraftRef = useRef<RichAircraft[]>(aircraft);

  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [labels, setLabels] = useState<ProjectedAircraft[]>([]);
  const [cards, setCards] = useState<{ label: string; x: number }[]>([]);
  const [fovX, setFovX] = useState(DEFAULT_FOV_X);
  const [fovY, setFovY] = useState(DEFAULT_FOV_Y);
  const [rangeNm, setRangeNm] = useState(DEFAULT_RANGE_NM);
  const fovXRef = useRef(fovX);
  const fovYRef = useRef(fovY);
  const rangeRef = useRef(rangeNm);
  const [pose, setPose] = useState<DevicePose>({ azimuth: 0, elevation: 0, roll: 0 });
  const [userPos, setUserPos] = useState<{ lat: number; lon: number } | null>(null);
  const [controlsOpen, setControlsOpen] = useState(false);

  useEffect(() => {
    fovXRef.current = fovX;
    fovYRef.current = fovY;
    rangeRef.current = rangeNm;
  }, [fovX, fovY, rangeNm]);

  useEffect(() => {
    aircraftRef.current = aircraft;
  }, [aircraft]);

  // Video element only mounts when active=true, so bind the stream after mount.
  useEffect(() => {
    if (!active || !streamRef.current) return;
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = streamRef.current;
    void video.play().catch(() => {});
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    skyRendererRef.current = new SkyModelRenderer(canvas);
    const resize = () => {
      skyRendererRef.current?.resize(canvas.clientWidth, canvas.clientHeight);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => {
      ro.disconnect();
      skyRendererRef.current?.dispose();
      skyRendererRef.current = null;
    };
  }, [active]);

  const stop = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    window.removeEventListener('deviceorientation', onOrient, true);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    skyRendererRef.current?.dispose();
    skyRendererRef.current = null;
    setActive(false);
    setArMode('off');
  };

  const onOrient = (e: DeviceOrientationEvent) => {
    const next = orientationToPose(e);
    poseRef.current = next;
    setPose(next);
  };

  const start = async () => {
    setError(null);
    try {
      const D = window.DeviceOrientationEvent as unknown as {
        requestPermission?: () => Promise<string>;
      };
      if (D && typeof D.requestPermission === 'function') {
        const r = await D.requestPermission();
        if (r !== 'granted') {
          setError('Motion & orientation access is required for Sky AR.');
          return;
        }
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Camera API unavailable. Use HTTPS Safari on iOS.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      streamRef.current = stream;

      if (!navigator.geolocation?.getCurrentPosition) {
        setError('Geolocation unavailable.');
        return;
      }
      const gotPos = (pos: GeolocationPosition) => {
        const u = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          altM: pos.coords.altitude ?? 0,
        };
        userRef.current = u;
        setUserPos({ lat: u.lat, lon: u.lon });
      };
      navigator.geolocation.getCurrentPosition(gotPos, (e) => setError(`Location: ${e.message}`), {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000,
      });
      watchIdRef.current = navigator.geolocation.watchPosition(gotPos, () => {}, {
        enableHighAccuracy: true,
        maximumAge: 5000,
      });

      window.addEventListener('deviceorientation', onOrient, true);

      setActive(true);
      setArMode('sky-hud');
      runLoop();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    }
  };

  const runLoop = () => {
    const tick = () => {
      const user = userRef.current;
      const currentPose = poseRef.current;
      if (user) {
        const list = aircraftRef.current;
        const aircraftByIcao = new Map<string, RichAircraft>();
        const projected: ProjectedAircraft[] = [];
        for (const a of list) {
          aircraftByIcao.set(a.icao, a);
          const p = projectAircraft(user, a, currentPose, fovXRef.current, fovYRef.current, rangeRef.current);
          if (p && p.inView) projected.push(p);
        }
        projected.sort((a, b) => a.distanceNm - b.distanceNm);
        const visible = projected.slice(0, 24);
        setLabels(visible);
        setCards(cardinals(currentPose.azimuth, fovXRef.current));
        skyRendererRef.current?.update({
          pose: currentPose,
          fovXDeg: fovXRef.current,
          fovYDeg: fovYRef.current,
          projected: visible,
          aircraftByIcao,
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    window.__fsStartAr = start;
    return () => {
      window.__fsStartAr = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (active) {
    return (
      <div className="fixed inset-0 z-[60] bg-black">
        <video ref={videoRef} className="absolute inset-0 h-full w-full object-cover" muted playsInline autoPlay />
        <canvas ref={canvasRef} className="absolute inset-0 h-full w-full pointer-events-none" aria-hidden />
        <div className="pointer-events-none absolute inset-0">
          {cards.map((c) => (
            <div
              key={c.label}
              className="absolute top-2 -translate-x-1/2 font-mono text-xs text-accent/80"
              style={{ left: `${c.x * 100}%` }}
            >
              {c.label}
            </div>
          ))}
          {labels.map((l) => (
            <div
              key={l.icao}
              className="hud-label"
              style={{ left: `${l.x * 100}%`, top: `${l.y * 100}%` }}
            >
              <div className="rounded-md bg-bg-panel/85 px-2 py-1 text-left shadow-lg ring-1 ring-bg-border backdrop-blur">
                <div className="text-[13px] font-semibold text-slate-100">{l.callsign ?? l.icao.toUpperCase()}</div>
                <div className="font-mono text-[10px] text-slate-300">
                  {fmtAlt(l.altFt)} · {fmtDist(l.distanceNm)} · {Math.round(l.elevationDeg)}°
                </div>
              </div>
            </div>
          ))}
          {labels.length === 0 && (
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-sm text-slate-400">
              Point your phone at the sky to find aircraft overhead.
            </div>
          )}
        </div>

        {/* Top bar */}
        <div className="absolute left-3 right-3 top-[calc(var(--safe-top)+8px)] flex items-center justify-between">
          <button onClick={stop} className="rounded-full bg-bg-panel/90 px-3 py-1.5 text-sm text-slate-100 ring-1 ring-bg-border">
            ✕ Close
          </button>
          <div className="rounded-full bg-bg-panel/90 px-3 py-1.5 text-xs text-slate-300 ring-1 ring-bg-border">
            {labels.length} in view
          </div>
          <button
            onClick={() => setControlsOpen((v) => !v)}
            className="rounded-full bg-bg-panel/90 px-3 py-1.5 text-sm text-slate-100 ring-1 ring-bg-border"
          >
            ⚙
          </button>
        </div>

        {/* Minimap */}
        {userPos && (
          <div className="absolute bottom-[calc(var(--safe-bottom)+12px)] left-3">
            <ArMinimap
              user={userPos}
              aircraft={aircraft}
              azimuth={pose.azimuth}
              fovXDeg={fovX}
              rangeNm={rangeNm}
            />
          </div>
        )}

        {/* Controls panel */}
        {controlsOpen && (
          <div className="absolute bottom-[calc(var(--safe-bottom)+12px)] right-3 left-[140px] max-w-xs rounded-xl bg-bg-panel/95 p-3 ring-1 ring-bg-border backdrop-blur">
            <div className="mb-2 text-xs font-semibold text-slate-300">AR settings</div>

            <label className="mb-2 block text-[11px] text-slate-400">
              Range {rangeNm} nm
              <input
                type="range"
                min={5}
                max={150}
                step={5}
                value={rangeNm}
                onChange={(e) => setRangeNm(Number(e.target.value))}
                className="mt-1 block w-full"
              />
            </label>

            <label className="mb-2 block text-[11px] text-slate-400">
              FOV horizontal {fovX}°
              <input
                type="range"
                min={30}
                max={120}
                step={2}
                value={fovX}
                onChange={(e) => setFovX(Number(e.target.value))}
                className="mt-1 block w-full"
              />
            </label>

            <label className="block text-[11px] text-slate-400">
              FOV vertical {fovY}°
              <input
                type="range"
                min={20}
                max={90}
                step={2}
                value={fovY}
                onChange={(e) => setFovY(Number(e.target.value))}
                className="mt-1 block w-full"
              />
            </label>
          </div>
        )}

        {error && (
          <div className="absolute inset-x-4 top-1/3 rounded-lg bg-bad/80 p-3 text-center text-sm text-white">
            {error}
            <button onClick={stop} className="mt-2 block w-full rounded bg-white/20 py-1">Dismiss</button>
          </div>
        )}
      </div>
    );
  }

  if (arMode !== 'sky-hud') return null;
  return (
    <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-bg/95 px-6 text-center">
      <div className="text-3xl">🛩️</div>
      <h2 className="mt-3 text-lg font-semibold text-slate-100">Sky AR</h2>
      <p className="mt-1 max-w-xs text-sm text-slate-400">
        Point your phone at the sky to see live 3D aircraft models and labels overlaid on the camera.
        Needs camera, motion, and location access.
      </p>
      <button
        onClick={start}
        className="mt-5 rounded-xl bg-accent px-6 py-3 text-sm font-semibold text-bg"
      >
        Start camera AR
      </button>
      <button onClick={() => setArMode('off')} className="mt-3 text-sm text-slate-400">
        Cancel
      </button>
      {error && <div className="mt-4 max-w-xs rounded-md bg-bad/20 p-3 text-xs text-red-300">{error}</div>}
    </div>
  );
}
